import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { PDAuthService } from 'pdauth';
import { Subscription } from 'rxjs';

import { BooleanConverter } from '../../../inputConverter';
import { SystemMessagesService } from '../../../services/system-messages.service';
import { ISystemMessage, ISystemMessageCategory, IUserSystemMessageStatus } from '../../../types';
import { BaseComponent } from '../../base.component';
import { ITableColumnSettings } from '../table/table-types';

const dismissedCategoryName = 'Dismissed';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-system-messages',
    styleUrls: ['../../sharedStyles/base.scss', './system-messages.component.scss'],
    templateUrl: './system-messages.component.html'
})
export class SystemMessagesComponent extends BaseComponent {
    @Output() readonly unreadMessages: EventEmitter<number> = new EventEmitter<number>();
    @Output() readonly adminMode: EventEmitter<boolean> = new EventEmitter<boolean>();

    private _active: boolean = false;
    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        this._active ? this.activate() : this.deactivate();
    }

    categories: ISystemMessageCategory[] = [];

    private _selectedCategory: number = 0;
    get selectedCategory() {
        return this._selectedCategory;
    }
    set selectedCategory(val: number) {
        this._selectedCategory = val;
        this.cdr.detectChanges();
    }

    dismissedCategoryId: number = -1;

    userMessages: IUserSystemMessageStatus[] = [];

    get filteredUserMessages() {
        let retVal = [];
        switch (this.selectedCategory) {
            case 0:
                retVal = this.userMessages.filter(message => !message.isDismissed);
                break;
            case this.dismissedCategoryId:
                retVal = this.userMessages.filter(message => message.isDismissed);
                break;
            default:
                retVal = this.userMessages.filter(message => !message.isDismissed && message.systemMessage.systemMessageCategoryId === this.selectedCategory);
                break;
        }
        return retVal;
    }

    editorConfig;
    messageTableSettings: ITableColumnSettings[];

    constructor(public _vcr: ViewContainerRef,
                private readonly systemMessagesService: SystemMessagesService,
                private readonly pdauth: PDAuthService,
                private readonly cdr: ChangeDetectorRef) {
        super(_vcr, true);
        this.activate();
    }

    private userSubscription: Subscription;
    private permissionsSubsciption: Subscription;
    private userId: number;

    private setSubscriptions() {
        this.userSubscription = this.pdauth.getLoggedInUser().subscribe(async user => {
            if (user) {
                this.userId = user.id;
                await this.setCategories();
                this.getMessages();
            }
        });

        this.permissionsSubsciption = this.pdauth.getLoggedInUserPermissions().subscribe(permissions => {
            if (permissions &&  permissions.includes('System Messages Admin')) {
                this._isAdmin = true;
                this.cdr.detectChanges();
            }
        });
    }

    private async setCategories() {
        this.categories = [{ categoryName: 'All Active', id: 0 }];
        this.categories.push(...(await this.systemMessagesService.getSystemCategories()));
        this.dismissedCategoryId = Math.max(...this.categories.map(category => category.id)) + 1;
        this.categories.push({
            categoryName: dismissedCategoryName,
            id: this.dismissedCategoryId
        });
        for (const category of this.categories) {
            if (category.categoryName.startsWith('System')) {
                category.categoryShortName = category.categoryName.replace('System ', '');
            } else {
                category.categoryShortName = category.categoryName;
            }
        }
        this.cdr.detectChanges();
    }

    private async getMessages() {
        if (this.userId) {
            this.userMessages = (await this.systemMessagesService.getUserMessageInbox(this.userId)).filter(um => {
                let valid = !!um.systemMessage;
                const now = new Date();
                if (um.systemMessage.expirationDate && new Date(um.systemMessage.expirationDate) < now) {
                    valid = false;
                }
                if (um.systemMessage.startDate && new Date(um.systemMessage.startDate) > now) {
                    valid = false;
                }
                return valid;
            });
            for (const message of this.userMessages) {
                message.systemMessage.systemMessageCategory = this.categories.find(cat => cat.id === message.systemMessage.systemMessageCategoryId);
                if (message.systemMessage.systemMessageCategory.categoryName.toLowerCase().includes('outage')) {
                    message.isOutage = true;
                }
            }
            this.getCount();
            this.cdr.detectChanges();
        }
    }

    private getCount() {
        const unreadMessages = this.userMessages.filter(message => !message.isDismissed && !message.isRead);
        this.unreadMessages.emit(unreadMessages.length);
    }

    private async activate() {
        this.setSubscriptions();

        this.editorConfig = {
            menubar: false,
            plugins: 'lists link',
            toolbar: 'bold | italic | underline | strikethrough | bullist | numlist | indent | outdent | link | unlink'
        };

        this.messageTableSettings = [
            { canFilter: true, canSort: true, dataType: 'string', propertyName: 'categoryShortName', title: 'Category' },
            { canFilter: true, canSort: true, dataType: 'string', propertyName: 'title', title: 'Title' },
            { canFilter: true, canSort: true, dataFilter: 'shortDate', dataType: 'date', propertyName: 'startDate', title: 'Started' },
            { canFilter: true, canSort: true, dataFilter: 'short', dataType: 'date', propertyName: 'expirationDate', title: 'Expires' },
            { canFilter: true, canSort: true, dataFilter: 'short', dataType: 'date', propertyName: 'createDate', title: 'Created' },
            { canFilter: true, canSort: false, dataType: 'html', propertyName: 'message', title: 'Message' },
            { buttons: [{ event: 'editMessage', title: 'Edit' }, { event: 'removeMessage', title: 'Remove' }], dataType: 'button', title: 'Options' }
        ];
    }

    private deactivate() {
        this.userSubscription.unsubscribe();
        this.permissionsSubsciption.unsubscribe();
        this.userId = null;
        this.showAdmin = false;
        this.cancel();
    }

    async dismissMessage(message: IUserSystemMessageStatus) {
        await this.systemMessagesService.markUserMessageDismissed(this.userId, message.systemMessageId);
        message.isDismissed = true;
        this.getCount();
        this.cdr.detectChanges();
    }

    toggleOpen(message: IUserSystemMessageStatus) {
        message.show = !message.show;
        this.markAsRead(message);
    }

    async markAsRead(message: IUserSystemMessageStatus) {
        if (!message.isRead) {
            await this.systemMessagesService.markUserMessageRead(this.userId, message.systemMessageId);
            message.isRead = true;
            this.getCount();
            this.cdr.detectChanges();
        }
    }

    setSelectedCategory(systemMessageCategoryId: number) {
        this.selectedCategory = systemMessageCategoryId;
    }

    // Admin
    private _isAdmin = false;
    get isAdmin() {
        return this._isAdmin;
    }

    private _showAdmin = false;
    get showAdmin() {
        return this._showAdmin;
    }
    set showAdmin(val: boolean) {
        if (this.isAdmin) {
            this._showAdmin = val;
            if (val) {
                this.loadAllMessages();
            } else {
                this.getMessages();
                this.getCount();
            }
            this.adminMode.emit(this._showAdmin);
            this.cdr.detectChanges();
        }
    }

    get dataCategories() {
        return this.categories.filter(c => c.id !== 0 && c.id !== this.dismissedCategoryId);
    }

    addEdit = false;

    async onMessageTableButtonClick(event) {
        if (event.detail.event === 'editMessage') {
            this.currentMessage = event.detail.data;
            this.addEdit = true;
        } else if (event.detail.event === 'removeMessage') {
            await this.systemMessagesService.deleteSystemMessage(event.detail.data.id);
            await this.loadAllMessages();
        }
    }

    updateDateString(e: any) {
        return new Date(e);
    }

    allSystemMessages: ISystemMessage[] = [];
    filteredSystemMessages: ISystemMessage[] = null;

    currentMessage: ISystemMessage = {} as any;

    async loadAllMessages() {
        this.allSystemMessages = await this.systemMessagesService.getAllMessages();
        for (const message of this.allSystemMessages) {
            message.systemMessageCategory = this.categories.find(cat => cat.id === message.systemMessageCategoryId);
            if (message.systemMessageCategory.categoryName.startsWith('System')) {
                message.systemMessageCategory.categoryShortName = message.systemMessageCategory.categoryShortName.replace('System ', '');
            }
            (message as any).categoryShortName = message.systemMessageCategory.categoryShortName;
        }

        this.filteredSystemMessages = [...this.allSystemMessages];
        this.cdr.detectChanges();
    }

    async save() {
        delete (this.currentMessage as any).categoryShortName;

        if (!this.currentMessage.id) {
            await this.systemMessagesService.createSystemMessage(this.currentMessage, this.userId);
        } else {
            await this.systemMessagesService.saveSystemMessage(this.currentMessage, this.userId);
        }
        this.addEdit = false;
        this.currentMessage = null;
        await this.loadAllMessages();
    }

    cancel() {
        this.addEdit = false;
        this.currentMessage = null;
    }

    newMessage() {
        this.currentMessage = {
            message: '',
            startDate: new Date(),
            title: ''
        } as any;
        this.addEdit = true;
    }

    toggleShowAdmin() {
        this.showAdmin = !this.showAdmin;
    }
}
